import { toast } from './dialog.js';

const VAULT_ITEM_MIME = 'application/x-vault-item-id';

/**
 * Makes an element accept file drops with visual feedback.
 * Tears down any previous drop zone on the same element to prevent duplicate listeners.
 * @param {HTMLElement} el - Drop target element
 * @param {(files: FileList) => void} onDrop - Callback with dropped files
 */
export function setupFileDropZone(el, onDrop) {
  // Tear down previous drop zone on this element if any
  if (el._vaultDropTeardown) el._vaultDropTeardown();

  let enterCount = 0;

  const onDragEnter = (e) => {
    if (e.dataTransfer.types.includes(VAULT_ITEM_MIME)) return;
    if (!e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
    enterCount++;
    if (enterCount === 1) el.classList.add('drop-target-active');
  };

  const onDragOver = (e) => {
    if (e.dataTransfer.types.includes(VAULT_ITEM_MIME)) return;
    if (!e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const onDragLeave = (e) => {
    if (!e.dataTransfer.types.includes('Files')) return;
    enterCount--;
    if (enterCount <= 0) {
      enterCount = 0;
      el.classList.remove('drop-target-active');
    }
  };

  const onDropHandler = (e) => {
    enterCount = 0;
    el.classList.remove('drop-target-active');

    if (e.dataTransfer.types.includes(VAULT_ITEM_MIME)) return;
    if (!e.dataTransfer.files.length) return;
    e.preventDefault();
    e.stopPropagation();

    const files = e.dataTransfer.files;
    const hasDir = Array.from(files).some(f => !f.type && !f.name.includes('.') && f.size === 0);
    if (hasDir) {
      toast('Directories not supported');
      return;
    }

    onDrop(files);
  };

  el.addEventListener('dragenter', onDragEnter);
  el.addEventListener('dragover', onDragOver);
  el.addEventListener('dragleave', onDragLeave);
  el.addEventListener('drop', onDropHandler);

  el._vaultDropTeardown = () => {
    el.removeEventListener('dragenter', onDragEnter);
    el.removeEventListener('dragover', onDragOver);
    el.removeEventListener('dragleave', onDragLeave);
    el.removeEventListener('drop', onDropHandler);
    delete el._vaultDropTeardown;
  };
}

/**
 * Makes an item card draggable with a custom MIME type.
 * @param {HTMLElement} cardEl - The item card element
 * @param {string} itemId - The item's UUID
 */
export function setupItemDragSource(cardEl, itemId) {
  cardEl.setAttribute('draggable', 'true');
  cardEl.addEventListener('dragstart', (e) => {
    e.dataTransfer.setData(VAULT_ITEM_MIME, itemId);
    e.dataTransfer.setData('text/plain', itemId);
    e.dataTransfer.effectAllowed = 'copyMove';
    cardEl.style.opacity = '0.5';
  });
  cardEl.addEventListener('dragend', () => {
    cardEl.style.opacity = '';
  });
}

/**
 * Makes a collection tile accept item card drags.
 * Tears down any previous item drop target on the same element.
 * @param {HTMLElement} tileEl - The collection tile element
 * @param {(itemId: string) => void} onItemDrop - Callback with the dropped item ID
 */
export function setupItemDropTarget(tileEl, onItemDrop) {
  if (tileEl._vaultItemDropTeardown) tileEl._vaultItemDropTeardown();

  let enterCount = 0;

  const onDragEnter = (e) => {
    if (!e.dataTransfer.types.includes(VAULT_ITEM_MIME)) return;
    e.preventDefault();
    enterCount++;
    if (enterCount === 1) tileEl.classList.add('drop-target-active');
  };

  const onDragOver = (e) => {
    if (!e.dataTransfer.types.includes(VAULT_ITEM_MIME)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const onDragLeave = () => {
    enterCount--;
    if (enterCount <= 0) {
      enterCount = 0;
      tileEl.classList.remove('drop-target-active');
    }
  };

  const onDropHandler = (e) => {
    enterCount = 0;
    tileEl.classList.remove('drop-target-active');

    const itemId = e.dataTransfer.getData(VAULT_ITEM_MIME);
    if (!itemId) return;
    e.preventDefault();
    e.stopPropagation();
    onItemDrop(itemId);
  };

  tileEl.addEventListener('dragenter', onDragEnter);
  tileEl.addEventListener('dragover', onDragOver);
  tileEl.addEventListener('dragleave', onDragLeave);
  tileEl.addEventListener('drop', onDropHandler);

  tileEl._vaultItemDropTeardown = () => {
    tileEl.removeEventListener('dragenter', onDragEnter);
    tileEl.removeEventListener('dragover', onDragOver);
    tileEl.removeEventListener('dragleave', onDragLeave);
    tileEl.removeEventListener('drop', onDropHandler);
    delete tileEl._vaultItemDropTeardown;
  };
}
